import React, { useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { css } from '@emotion/core';
import i18n from 'i18next';
import { isSmallPromoWidth } from 'config/promoGameTypes';
import { scrollPromoBodyIntoView } from 'client/utils/scroll';
import { togglePrizeList } from 'client/redux/modules/flags';
import { IS_IFRAME_MODE, IFRAME_MODE_MIN_HEIGHT } from 'config/envVariable';
import { postHeightMessage } from 'client/hooks/useHeightResizePostMessage';

import { useIsModuleNeedVerify } from 'client/hooks/moduleVerifyUtilHooks';
import RedeemIcon from '@material-ui/icons/Redeem';
import PopupWithFixHeader from 'client/components/PopupWithFixHeader';
import PrizeList from 'client/components/Result/prizeList/PrizeList';
import FeverBadgeButton from 'components/Button/FeverBadgeButton';

function ViewPrizeListButton({ btnStyle, isLoading, onToPrize = () => {} }, ref) {
    const dispatch = useDispatch();
    const isModuleNeedVerify = useIsModuleNeedVerify();
    const { type: gameType } = useSelector(s => s.promo);
    const isPrizeListOpen = useSelector(s => s.flags.isPrizeListOpen);
    const { isMgmShare, isMgmSelf } = useSelector(s => s.flags);
    const { userGameResultRecords: mgmUserGameResultRecords = [] } = useSelector(
        s => s.mgmReferral
    );
    const userGameResultRecords = useSelector(s => s.userGameResultRecords);
    const entryIdMappingGameRecords = getEntryIdMappingGameRecordsSelector(userGameResultRecords);
    const entryIdMappingMGMGameRecords = getEntryIdMappingGameRecordsSelector(
        mgmUserGameResultRecords
    );

    const { winnerPrizes = [] } = useSelector(s => s.userWinnerPrize);
    const { userWinnerPrize: { winnerPrizes: mgmWinnerPrize = [] } = {} } = useSelector(
        s => s.mgmReferral
    );
    const currentWinnerPrize = Boolean(isMgmShare)
        ? mgmWinnerPrize
        : Boolean(isMgmSelf)
        ? [...mgmWinnerPrize, ...winnerPrizes]
        : winnerPrizes;

    const validWinnerList = currentWinnerPrize.filter(w => {
        const recordOfWinner = entryIdMappingGameRecords[w.entryId];
        const mgmRecordOfWinner = entryIdMappingMGMGameRecords[w.entryId];
        if (mgmRecordOfWinner) {
            if (mgmRecordOfWinner?.deletedAt) {
                return false;
            } else {
                return true;
            }
        } else {
            if (recordOfWinner?.deletedAt) {
                return false;
            } else if (isModuleNeedVerify && !recordOfWinner?.flow?.qualified) {
                return false;
            } else {
                return true;
            }
        }
    });

    const winnerLength = validWinnerList.length;

    function showPrizeList() {
        dispatch(togglePrizeList(true));
    }
    function closePrizeList() {
        dispatch(togglePrizeList(false));
    }
    function handleToPrize({ prizeUUID, entriesUUID }) {
        scrollPromoBodyIntoView();
        onToPrize({ prizeUUID, entriesUUID });
    }
    function handleEffectEntered() {
        if (IS_IFRAME_MODE) {
            const targetElement = document.getElementById('promo-prize-list');
            if (targetElement) {
                const height = targetElement.clientHeight;
                postHeightMessage(Math.max(height, IFRAME_MODE_MIN_HEIGHT));
            }
        }
    }
    function handleEffectExited() {
        if (IS_IFRAME_MODE) {
            const targetElement = document.getElementById('promo-client-body');
            if (targetElement) {
                const height = targetElement.clientHeight;
                postHeightMessage(Math.max(height, IFRAME_MODE_MIN_HEIGHT));
            }
        }
    }

    useImperativeHandle(ref, () => ({
        getWinnerPrizeCount: () => winnerLength
    }));

    if (winnerLength === 0) {
        return null;
    }

    const handleShowPrize = () => {
        const [{ entryUuid: entriesUUID, prize: { uuid: prizeUUID } = {} }] = validWinnerList;
        handleToPrize({ prizeUUID, entriesUUID });
    };

    return (
        <>
            <div css={btnContainerStyle}>
                <FeverBadgeButton
                    onClick={winnerLength === 1 ? handleShowPrize : showPrizeList}
                    disabled={isLoading}
                    Icon={RedeemIcon}
                    number={winnerLength}
                    numberStyle={btnStyle.numberStyle}
                    buttonSyle={btnStyle.buttonSyle}
                    buttonHoverStyle={btnStyle.buttonHoverStyle}
                />
            </div>
            <PopupWithFixHeader
                show={isPrizeListOpen}
                title={i18n.t('btn.check_prize')}
                onEffectEntered={handleEffectEntered}
                onEffectExited={handleEffectExited}
                onClose={closePrizeList}
                maxWidth={isSmallPromoWidth(gameType) ? 720 : 960}
            >
                <PrizeList
                    items={validWinnerList}
                    onClick={(e, { entryUuid: entriesUUID, prize: { uuid: prizeUUID } = {} }) =>
                        handleToPrize({ prizeUUID, entriesUUID })
                    }
                />
            </PopupWithFixHeader>
        </>
    );
}

export default React.forwardRef(ViewPrizeListButton);

const getEntryIdMappingGameRecordsSelector = userGameResultRecords =>
    userGameResultRecords.reduce((obj, record) => {
        obj[record.id] = record;
        return obj;
    }, {});

const btnContainerStyle = css`
    padding: 0 4px;
`;
